import { useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * What this deployment can do, as the server reports it.
 *
 * Fetched once and shared, because several components need the same answer and
 * none of them should make its own request for it — the navigation asks on every
 * page, and a per-component fetch would be one request per render tree.
 */
/** A value with a label, as the server sends its vocabularies. */
export interface Choice {
  value: string;
  label: string;
}

export interface ClientConfig {
  billingEnabled: boolean;
  /**
   * Vocabularies the forms render, served rather than duplicated here. Default
   * to empty: a form that renders no options is visibly incomplete, whereas a
   * stale hard-coded copy looks correct while disagreeing with the server.
   */
  subjects: Choice[];
  audienceBands: Choice[];
  referralSources: Choice[];
  dialCodes: Choice[];
}

/**
 * The safe assumption while the answer is in flight, and if it never arrives.
 *
 * Billing off, because the cost of guessing wrong in each direction is not
 * symmetric: hiding Plans for a second on a deployment that does charge is
 * invisible, while showing Plans and Invoices on one that does not is two dead
 * ends on the first page a teacher sees.
 */
const ASSUMED: ClientConfig = {
  billingEnabled: false,
  subjects: [],
  audienceBands: [],
  referralSources: [],
  dialCodes: [],
};

let cached: ClientConfig | null = null;
let inFlight: Promise<ClientConfig> | null = null;

async function load(): Promise<ClientConfig> {
  if (cached) return cached;
  /*
   * De-duplicated: three components mounting together would otherwise make three
   * identical requests before any of them resolved.
   */
  inFlight ??= api
    .get<ClientConfig>('/api/client-config')
    .then((config) => {
      cached = config;
      return config;
    })
    .catch(() => ASSUMED);

  return inFlight;
}

/** The deployment's capabilities. Never suspends and never throws. */
export function useClientConfig(): ClientConfig {
  const [config, setConfig] = useState<ClientConfig>(cached ?? ASSUMED);

  useEffect(() => {
    let active = true;
    void load().then((loaded) => {
      if (active) setConfig(loaded);
    });
    return () => {
      active = false;
    };
  }, []);

  return config;
}
